import React from 'react';
import {
  Users,
  DollarSign,
  Calendar,
  Package,
  CreditCard,
  CheckCircle,
  ArrowUpRight,
  ArrowDownRight,
  Lock,
  Crown,
} from 'lucide-react';
import { PRIVATE_PAYOUT_EXCLUDED_NOTE } from '../../../utils/bookingSource';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === '') return '$0.00';
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  if (isNaN(numAmount) || !isFinite(numAmount)) return '$0.00';
  return `$${numAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const toneStyles = {
  emerald: {
    wrap: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400',
  },
  sky: {
    wrap: 'bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-400',
  },
  amber: {
    wrap: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400',
  },
  gray: {
    wrap: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
  },
};

const StatCard = ({ title, value, subtitle, icon: Icon, tone = 'gray', change }) => {
  const styles = toneStyles[tone] || toneStyles.gray;
  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className={`p-2 rounded-xl shrink-0 ${styles.wrap}`}>
          <Icon className="w-4 h-4" />
        </div>
        {change !== null && change !== undefined ? (
          <div
            className={`inline-flex items-center gap-0.5 text-[11px] font-semibold tabular-nums ${
              change >= 0
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-red-600 dark:text-red-400'
            }`}
          >
            {change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(change).toFixed(1)}%
          </div>
        ) : null}
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {title}
      </p>
      <p className="mt-1 text-xl sm:text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50 tabular-nums">
        {value}
      </p>
      {subtitle ? (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-snug">{subtitle}</p>
      ) : null}
    </div>
  );
};

const KeyStatistics = ({ analytics, amountsVisible = true }) => {
  const overview = analytics?.overview || {};
  const bookingsAnalytics = analytics?.bookings_analytics || {};
  const revenueAnalytics = analytics?.revenue_analytics || {};
  const userAnalytics = analytics?.user_analytics || {};
  const vendorAnalytics = analytics?.vendor_analytics || {};

  const privateOffline =
    overview.private_offline_collected ?? revenueAnalytics.private_offline_collected ?? 0;
  const privateCount = overview.private_bookings_count ?? 0;

  const primary = [
    {
      key: 'revenue',
      isAmount: true,
      title: 'Platform revenue',
      value: formatCurrency(
        overview.platform_revenue ?? overview.total_revenue ?? revenueAnalytics.total_revenue ?? 0
      ),
      subtitle: 'Excludes private bookings',
      icon: DollarSign,
      tone: 'emerald',
      change: revenueAnalytics.revenue_growth ?? null,
    },
    {
      key: 'subscriptions',
      isAmount: true,
      title: 'Subscriptions',
      value: formatCurrency(overview.subscription_revenue ?? 0),
      subtitle: 'SaaS payments collected',
      icon: Crown,
      tone: 'sky',
    },
    {
      key: 'bookings',
      isAmount: false,
      title: 'Bookings',
      value: overview.total_bookings || bookingsAnalytics.total || 0,
      icon: Calendar,
      tone: 'sky',
      change: bookingsAnalytics.growth_rate ?? null,
    },
    {
      key: 'fees',
      isAmount: true,
      title: 'Platform fees',
      value: formatCurrency(overview.platform_fees || revenueAnalytics.platform_fees || 0),
      subtitle: 'Commission retained',
      icon: CreditCard,
      tone: 'amber',
    },
    {
      key: 'completed',
      isAmount: false,
      title: 'Completed',
      value: overview.completed_bookings || bookingsAnalytics.by_status?.completed || 0,
      subtitle: 'Platform events finished',
      icon: CheckCircle,
      tone: 'emerald',
    },
  ];

  const secondary = [
    {
      key: 'subscribers',
      isAmount: false,
      title: 'Subscribed vendors',
      value: overview.active_subscribers ?? 0,
      subtitle:
        overview.subscribers_in_period != null
          ? `${overview.subscribers_in_period} started this period`
          : 'Active paid/free plans',
      icon: Crown,
      tone: 'gray',
    },
    {
      key: 'users',
      isAmount: false,
      title: 'Users',
      value: overview.total_users || userAnalytics.total_users || 0,
      icon: Users,
      tone: 'gray',
      change: userAnalytics.growth_rate ?? null,
    },
    {
      key: 'vendors',
      isAmount: false,
      title: 'Vendors',
      value: overview.total_vendors || vendorAnalytics.total_vendors || 0,
      icon: Package,
      tone: 'gray',
      change: vendorAnalytics.growth_rate ?? null,
    },
  ];

  if (privateOffline > 0 || privateCount > 0) {
    secondary.push({
      key: 'private',
      isAmount: true,
      title: 'Private offline',
      value: formatCurrency(privateOffline),
      subtitle: `${privateCount} booking(s) · ${PRIVATE_PAYOUT_EXCLUDED_NOTE}`,
      icon: Lock,
      tone: 'sky',
    });
  }

  const visiblePrimary = amountsVisible ? primary : primary.filter((s) => !s.isAmount);
  const visibleSecondary = amountsVisible ? secondary : secondary.filter((s) => !s.isAmount);

  return (
    <div className="space-y-3">
      {!amountsVisible ? (
        <p className="text-xs text-gray-500 dark:text-gray-400 px-0.5">
          Amount cards are hidden. Use the eye control to show them again.
        </p>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {visiblePrimary.map(({ key, isAmount: _isAmount, ...stat }) => (
          <StatCard key={key} {...stat} />
        ))}
      </div>
      {visibleSecondary.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {visibleSecondary.map(({ key, isAmount: _isAmount, ...stat }) => (
            <StatCard key={key} {...stat} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default KeyStatistics;
